import { Box, Divider, LinearProgress, Typography } from '@mui/material';
import { IBudget, IExpense, Income } from 'common/entities';
import { useAppSelector } from 'core/hooks/hooks';
import { getMonthBudgetTotal } from 'core/services/budget.service';
import { getExpenseMonthTotal } from 'core/services/expense.service';
import { getMonthIncomeTotal } from 'core/services/income.service';
import { budgetMonthSelector } from 'core/store/selectors/monthBudget.selector';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useMemo } from 'react';
import BudgetMonthPanel from './BudgetMonthPanel';
import { budgetsSelector } from 'core/store/selectors/budgets.selector';
import { expensesSelector } from 'core/store/selectors/expenses.selector';
import { useCurrency } from 'core/hooks/useCurrency';

const SummaryRow = ({ name, value }: { name: string; value?: string }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      py: 0.5,
      alignItems: 'center'
    }}
  >
    <Typography>{name}</Typography>
    {value ? (
      <Typography sx={{ fontWeight: 500 }}>{value}</Typography>
    ) : (
      <Typography
        sx={{ fontSize: '0.8em', color: '#999' }}
      >{`<not set>`}</Typography>
    )}
  </Box>
);

export const BudgetMonthSummary = () => {
  const budgetMonth = useAppSelector(periodSelector);
  const { budgets } = useAppSelector(budgetsSelector);
  const { expenses } = useAppSelector(expensesSelector());
  const { prevPeriodResidue, selectedIncomes, selectedBudgets } =
    useAppSelector(budgetMonthSelector(budgetMonth));
  const { currency, sumWithCurrency } = useCurrency();

  const incomesTotal = useMemo(
    () => (selectedIncomes ? getMonthIncomeTotal(selectedIncomes) : undefined),
    [selectedIncomes]
  );

  const totalMonthBudget = useMemo(
    () =>
      selectedBudgets
        ? getMonthBudgetTotal(
            budgets.filter(b => !b.accumulation),
            selectedBudgets
          )
        : 0,
    [budgets, selectedBudgets]
  );

  const totalMonthBudgetSpent = useMemo(
    () => getExpenseMonthTotal(expenses, budgetMonth, currency),
    [expenses, budgetMonth, currency]
  );

  const totalMonthAccumulations = useMemo(
    () =>
      selectedBudgets
        ? getMonthBudgetTotal(
            budgets.filter(b => b.accumulation),
            selectedBudgets
          )
        : 0,
    [budgets, selectedBudgets]
  );

  const currentBudgetBalance = useMemo(
    () => totalMonthBudget - totalMonthBudgetSpent,
    [totalMonthBudget, totalMonthBudgetSpent]
  );

  const spentProgress = useMemo(
    () =>
      (totalMonthBudgetSpent / (totalMonthBudget ? totalMonthBudget : 1)) * 100,
    [totalMonthBudgetSpent, totalMonthBudget]
  );

  const endOfMonthBalance = useMemo(
    () =>
      incomesTotal
        ? incomesTotal -
          totalMonthBudgetSpent -
          totalMonthAccumulations +
          (prevPeriodResidue ?? 0)
        : currentBudgetBalance + (prevPeriodResidue ?? 0),
    [
      currentBudgetBalance,
      incomesTotal,
      totalMonthAccumulations,
      prevPeriodResidue
    ]
  );

  return (
    <BudgetMonthPanel title="Month Summary">
      <Box sx={{ mb: 2 }}>
        <SummaryRow
          name="Incomes month total:"
          value={incomesTotal ? sumWithCurrency(incomesTotal) : undefined}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <SummaryRow
          name="Total month budget:"
          value={sumWithCurrency(totalMonthBudget)}
        />
        <SummaryRow
          name="Total month budget spent:"
          value={sumWithCurrency(totalMonthBudgetSpent)}
        />
        <Divider />
        <SummaryRow
          name="Current budget balance:"
          value={sumWithCurrency(currentBudgetBalance)}
        />

        <LinearProgress
          variant="determinate"
          value={spentProgress}
          sx={{ height: '1.2rem', width: '100%', mt: 1 }}
        />
        {!!incomesTotal && (
          <>
            <SummaryRow
              name="Retained balance:"
              value={sumWithCurrency(endOfMonthBalance)}
            />
          </>
        )}
      </Box>

      <SummaryRow
        name="Total month accumulations:"
        value={sumWithCurrency(totalMonthAccumulations)}
      />
    </BudgetMonthPanel>
  );
};
